import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, FreeMode } from "swiper/modules";
import { motion } from "framer-motion";
import { Trans } from "react-i18next";

import "swiper/css";

const ChapterOptionsCoverflow = ({ goTo, extractChapterId, currentSection }) => {
  const swiperRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Ref para detectar taps vs drags usando Pointer Events (más robusto en Chrome 140)
  const pointerRef = useRef({ x: 0, y: 0, t: 0, type: '' });
  const ignoreNextClickRef = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const mq = window.matchMedia("(max-width: 767px)");
      const update = () => setIsMobile(mq.matches);
      update();
      mq.addEventListener ? mq.addEventListener("change", update) : mq.addListener(update);
      return () => {
        mq.removeEventListener ? mq.removeEventListener("change", update) : mq.removeListener(update);
      };
    }
  }, []);

  // Handlers para Pointer Events - más robustos en Chrome 140 que Touch Events
  const onSlidePointerDown = (e) => {
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
    pointerRef.current = { 
      x: e.clientX, 
      y: e.clientY, 
      t: performance.now(), 
      type: e.pointerType 
    };
  };

  const onSlidePointerUp = (option, index) => (e) => {
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
    
    const s = pointerRef.current;
    const dt = performance.now() - s.t;
    const moved = Math.hypot(e.clientX - s.x, e.clientY - s.y);
    const isTap = dt < 250 && moved < 12;
    
    if (!isTap) return;
    
    const swiper = swiperRef.current;
    if (!swiper || swiper.animating) return;
    
    ignoreNextClickRef.current = true;
    
    // Si es la slide activa, ejecuta la acción; si no, centra esa slide
    if (index === swiper.activeIndex) {
      option.onClick();
    } else {
      swiper.slideTo(index);
    }
  };

  const handleSafeClick = (fn) => () => {
    if (isDragging) return; // Evita clics fantasma al terminar un drag
    if (ignoreNextClickRef.current) {
      ignoreNextClickRef.current = false;
      return;
    }
    fn();
  };

  const options = [
    {
      key: "comic",
      image: "/images/comic.webp",
      labelKey: "comic",
      onClick: () => goTo(`${extractChapterId(currentSection)}-comic`),
    },
    {
      key: "articulos",
      image: "/images/articulo.webp",
      labelKey: "articles",
      onClick: () => goTo(`${extractChapterId(currentSection)}-articulos`),
    },
    {
      key: "extras",
      image: "/images/cofre.webp",
      labelKey: "extras",
      onClick: () => goTo(`${extractChapterId(currentSection)}-extras`),
    },
  ];

  return (
    <Swiper
      modules={[Mousewheel, FreeMode]}
      onSwiper={(s) => (swiperRef.current = s)}
      direction="vertical"
      effect="slide"
      slidesPerView={"auto"}
      centeredSlides
      // En móvil evitamos freeMode para mejorar la captura táctil y el snapping
      freeMode={isMobile ? false : { enabled: true, sticky: false, momentum: true, momentumRatio: 0.5, momentumVelocityRatio: 0.5, minimumVelocity: 0.05 }}
      mousewheel={{ forceToAxis: true, sensitivity: 0.8, releaseOnEdges: false }}
      speed={isMobile ? 500 : 800}
      grabCursor
      // Ajustes táctiles para mejorar la captura en móvil
      touchEventsTarget="container"
      passiveListeners={false}
      threshold={6}
      longSwipes
      longSwipesRatio={0.22}
      touchAngle={25}
      resistanceRatio={0.65}
      nested
      allowTouchMove
      roundLengths
      slideToClickedSlide
      observer
      observeParents
      watchSlidesProgress
      // Fix: evitar que Swiper mate los clicks por micro-drags
      preventClicks={false}
      preventClicksPropagation={false}
      touchStartPreventDefault={false}
      style={{
        height: "100%",
        overflow: "visible",
        overscrollBehaviorY: "contain",
        WebkitOverflowScrolling: "touch",
        willChange: "transform",
        touchAction: "pan-y",
      }}
      // Señales para suprimir clics fantasma y detectar drag
      onSliderMove={() => setIsDragging(true)}
      onTouchMove={() => setIsDragging(true)}
      onTouchEnd={() => setTimeout(() => setIsDragging(false), 40)}
      onTransitionEnd={() => setIsDragging(false)}
    >
      {options.map((option, index) => (
        <SwiperSlide
          key={option.key}
          onPointerDown={onSlidePointerDown}
          onPointerUp={onSlidePointerUp(option, index)}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: isMobile ? "300px" : "320px",
            margin: isMobile ? "12px 0" : "30px 0",
          }}
        >
          <motion.div
            className="cursor-pointer flex flex-col items-center"
            initial={{ scale: 0.9 }}
            whileHover={isMobile ? undefined : { scale: 1.05 }}
            whileTap={{ scale: 1 }}
            onClick={handleSafeClick(option.onClick)}
          >
            <img
              src={option.image}
              alt={option.key}
              className="w-80 h-80 object-contain" // Imágenes grandes en mobile
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
            />
            <h3 className="text-3xl font-semibold text-white mt-2">
              <Trans i18nKey={option.labelKey} />
            </h3>
          </motion.div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default ChapterOptionsCoverflow;
